import React from "react";
import { useDisplayFilters } from "./useDisplayFilters";
import { useDisplayFilterPreferences } from "../components/Project/EvolutionSubHeader/useDisplayFilterPreferences";
import useActiveAnalysesStream from "./useActiveAnalysesStream";

function useActiveAnalysesWithDisplayFilters(projectId: string) {
  const { activeAnalyses, isStreaming } = useActiveAnalysesStream(projectId);

  const displayFilters = useDisplayFilters(projectId);
  const { selectedGroupedFilterIds, selectedUngroupedFilterIds } =
    useDisplayFilterPreferences(projectId);
  const displayFiltersPreferences = React.useMemo(
    () => [...selectedGroupedFilterIds, ...selectedUngroupedFilterIds],
    [selectedGroupedFilterIds, selectedUngroupedFilterIds],
  );

  const filteredAnalyses = React.useMemo(() => {
    const filters = displayFilters.filter(df => displayFiltersPreferences.includes(df.id));
    if (filters.length === 0) return activeAnalyses;

    return activeAnalyses.flatMap(analysis => {
      let displayFilterMode = true;
      for (const [qId, qv] of Object.entries(analysis.quantityValues)) {
        const filterTargets = filters.filter(df => df.quantity.id === qId);
        if (filterTargets.length > 0) {
          const inOfMin = filterTargets.every(
            filter => filter.min === null || qv.measured >= filter.min,
          );
          const inOfMax = filterTargets.every(
            filter => filter.max === null || qv.measured <= filter.max,
          );

          const inOfStd = filterTargets.every(
            filter => filter.maxStd === null || qv.std === null || qv.std <= filter.maxStd,
          );

          if (!(inOfMin && inOfMax && inOfStd)) {
            displayFilterMode = false;
            break;
          }
        }
      }

      if (!displayFilterMode) return [];
      return [analysis];
    });
  }, [activeAnalyses, displayFilters, displayFiltersPreferences]);

  const displayFilterRemovesAllActiveAnalyses =
    filteredAnalyses.length === 0 && activeAnalyses.length > 0;

  return React.useMemo(
    () => ({
      isStreaming,
      displayFilterRemovesAllActiveAnalyses,
      activeAnalyses: filteredAnalyses,
    }),
    [filteredAnalyses, isStreaming, displayFilterRemovesAllActiveAnalyses],
  );
}

export default useActiveAnalysesWithDisplayFilters;
